import { useMemo, useState } from "react";
import { comics } from "@/mocks/comics";
import SectionHeader from "./SectionHeader";

type RankMode = "finish" | "popular" | "score";

const modeTabs: { key: RankMode; label: string }[] = [
  { key: "finish", label: "完结排行" },
  { key: "popular", label: "人气排行" },
  { key: "score", label: "高分排行" },
];

function changeIcon(change: string) {
  if (change === "up") {
    return <span className="flex h-5 w-5 items-center justify-center rounded bg-primary-100 text-primary-600"><i className="ri-arrow-up-line text-xs" aria-hidden="true" /></span>;
  }
  if (change === "down") {
    return <span className="flex h-5 w-5 items-center justify-center rounded bg-accent-100 text-accent-700"><i className="ri-arrow-down-line text-xs" aria-hidden="true" /></span>;
  }
  return <span className="flex h-5 w-5 items-center justify-center rounded bg-background-200 text-foreground-500"><i className="ri-drag-move-2-line text-xs" aria-hidden="true" /></span>;
}

export default function Ranking() {
  const [mode, setMode] = useState<RankMode>("finish");

  const list = useMemo(() => {
    let sorted = [...comics];
    if (mode === "finish") {
      sorted = sorted.filter((c) => c.status === "完结").sort((a, b) => b.rating - a.rating);
    } else if (mode === "popular") {
      sorted = sorted.sort((a, b) => b.likes - a.likes);
    } else {
      sorted = sorted.sort((a, b) => b.rating - a.rating);
    }
    return sorted.slice(0, 10);
  }, [mode]);

  return (
    <section id="ranking" className="w-full bg-background-100/60 py-14 md:py-20">
      <div className="mx-auto w-full max-w-7xl px-4 md:px-6">
        <SectionHeader
          eyebrow="完结排行榜"
          title="高分佳作排行榜，一榜帮你做选择"
          desc="综合读者评分、人气热度与完结度动态排序，完结排行榜助你快速挑选不用追更、可以一口气读完的心仪之作。"
        />

        <div className="mb-6 flex flex-wrap items-center gap-2">
          {modeTabs.map((t) => (
            <button
              key={t.key}
              type="button"
              onClick={() => setMode(t.key)}
              className={`rounded-full px-4 py-1.5 text-sm font-medium transition-colors ${
                mode === t.key
                  ? "bg-primary-500 text-white"
                  : "bg-background-200 text-foreground-700 hover:bg-background-300"
              }`}
              aria-pressed={mode === t.key}
            >
              {t.label}
            </button>
          ))}
        </div>

        <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
          {list.map((c, idx) => {
            const rank = idx + 1;
            return (
              <a
                key={`${mode}-${c.id}`}
                href="#hot"
                className="flex items-center gap-4 rounded-lg border border-background-200 bg-background-50 p-3 transition-all hover:border-primary-200 hover:bg-primary-50/40"
              >
                <span
                  className={`font-heading text-2xl w-9 text-center shrink-0 ${
                    rank === 1 ? "text-secondary-500" : rank === 2 ? "text-foreground-400" : rank === 3 ? "text-primary-400" : "text-foreground-300"
                  }`}
                >
                  {rank}
                </span>
                <img
                  src={c.cover}
                  alt={`${c.title} ${c.genre}漫画封面`}
                  className="h-20 w-14 shrink-0 rounded-md object-cover object-top"
                  loading="lazy"
                />
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <h3 className="truncate font-heading text-base text-foreground-950">{c.title}</h3>
                    {c.status === "完结" && (
                      <span className="shrink-0 rounded bg-secondary-100 px-1.5 py-0.5 text-[11px] font-semibold text-secondary-900">完结</span>
                    )}
                  </div>
                  <p className="mt-0.5 truncate text-xs text-foreground-600">
                    {c.author} · {c.genre} · {c.chapters}话
                  </p>
                  <div className="mt-1.5 flex items-center gap-3 text-xs text-foreground-500">
                    <span className="inline-flex items-center gap-1"><i className="ri-star-fill text-secondary-500" aria-hidden="true" />{c.rating.toFixed(1)}</span>
                    <span className="inline-flex items-center gap-1"><i className="ri-heart-3-fill text-primary-400" aria-hidden="true" />{(c.likes / 1000).toFixed(1)}k</span>
                    <span className="inline-flex items-center gap-1"><i className="ri-price-tag-3-line" aria-hidden="true" />{c.tags.slice(0, 2).join(" / ")}</span>
                  </div>
                </div>
                {mode === "popular" && changeIcon(["up", "down", "same", "up", "down", "same", "up", "down", "same", "up"][idx])}
              </a>
            );
          })}
        </div>
      </div>
    </section>
  );
}